import {
    faBalanceScaleRight,
    faEdit,
    faGlasses,
    faLayerGroup,
    faTrash,
    faUser
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useApi } from '@hooks/useApi';
import { Button } from '@ui';
import { Column, DataTable, DataTableContainer, InlineDropdownAction, TableActionButton } from 'components/DataTable';
import { useConfirmedCallback } from 'hooks/useConfirmedCallback';
import { useModal } from 'hooks/useModal';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AddRelativeForm } from '../AddRelativeForm';
import { EditRelativeForm } from '../EditRelativeForm';
import { PermissionForm } from '../PermissionForm';
import { PermissionGroupAssignmentForm } from '../PermissionGroupAssignmentForm';
import { PermissionPreview } from '../PermissionPreview';
import { Relative } from '../types';

interface Props {
    clubSlug: string;
}

export const RelativesList: React.FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<Props>>> = ({
    clubSlug
}) => {
    const { t } = useTranslation('general');
    const api = useApi();
    const [setModal, resetModal] = useModal();
    const asyncListRef = useRef(null);

    const columns: Column[] = [
        {
            uid: 'name',
            name: 'Name',
            // Renders the name, then the position (if set), then the email beneath it.
            renderer: (name, item) => (
                <div>
                    <span className="tw-block">
                        {item.self && <FontAwesomeIcon className="tw-mr-2" icon={faUser} />}
                        {name}
                    </span>
                    {item.email && <span className="tw-block tw-text-accents-7">{item.email}</span>}
                    {item.position && <span className="tw-block tw-text-xs tw-text-accents-6">{item.position}</span>}
                </div>
            )
        },
        {
            uid: 'full_access',
            name: 'Super Admin',
            renderer: (value, relative) => (
                <InlineDropdownAction
                    listRef={asyncListRef}
                    onAction={(key, resolve) => {
                        void api({
                            url: `club/${clubSlug}/relatives/${relative.id}`,
                            method: 'PUT',
                            data: { full_access: key === 'true' },
                            onSuccess: ({ data }) => resolve(data)
                        });
                    }}
                    value={String(Boolean(value))}
                    options={[
                        { value: 'true', label: t('common:choices.yes') },
                        { value: 'false', label: t('common:choices.no') }
                    ]}
                />
            )
        },
        {
            uid: 'visible',
            name: t('permissions.relativesList.columns.visible'),
            renderer: (value, relative) => (
                <InlineDropdownAction
                    listRef={asyncListRef}
                    onAction={(key, resolve) => {
                        void api({
                            url: `club/${clubSlug}/relatives/${relative.id}`,
                            method: 'PUT',
                            data: { visible: key === 'true' },
                            onSuccess: ({ data }) => resolve(data)
                        });
                    }}
                    value={String(Boolean(value))}
                    options={[
                        { value: 'true', label: t('common:choices.yes') },
                        { value: 'false', label: t('common:choices.no') }
                    ]}
                />
            )
        },
        {
            uid: 'notify',
            name: t('permissions.relativesList.columns.notifications'),
            renderer: (value, relative) => (
                <InlineDropdownAction
                    listRef={asyncListRef}
                    onAction={(key, resolve) => {
                        void api({
                            url: `club/${clubSlug}/relatives/${relative.id}`,
                            method: 'PUT',
                            data: { notify: key === 'true' },
                            onSuccess: ({ data }) => resolve(data)
                        });
                    }}
                    value={String(Boolean(value))}
                    options={[
                        { value: 'true', label: t('common:choices.yes') },
                        { value: 'false', label: t('common:choices.no') }
                    ]}
                />
            )
        },
        { uid: 'actions', name: t('permissions.groupManagement.columns.actions'), align: 'end' }
    ];

    const showPermissionForm = (relative: Relative) =>
        setModal({
            title: t('permissions.groupManagement.manageRights'),
            content: (
                <PermissionForm
                    relative={relative}
                    clubSlug={clubSlug}
                    callback={() => {
                        asyncListRef.current?.reload();
                        resetModal();
                    }}
                />
            ),
            show: true,
            size: 50
        });

    const showPermissionGroupAssignmentForm = (relative: Relative) =>
        setModal({
            title: t('permissions.relativesList.manageGroups'),
            content: (
                <PermissionGroupAssignmentForm
                    relative={relative}
                    clubSlug={clubSlug}
                    callback={() => {
                        asyncListRef.current?.reload();
                        resetModal();
                    }}
                />
            ),
            show: true
        });

    const showPermissionPreview = (relative?: Relative) =>
        setModal({
            title: t('permissions.relativesList.viewPermissions'),
            content: <PermissionPreview relative={relative} clubSlug={clubSlug} />,
            show: true,
            size: 70
        });

    const showAddRelativeForm = () =>
        setModal({
            title: t('permissions.relativesList.addUser'),
            content: (
                <AddRelativeForm
                    clubSlug={clubSlug}
                    callback={() => {
                        asyncListRef.current?.reload();
                        resetModal();
                    }}
                />
            ),
            show: true
        });

    // Opens the edit-relative modal (currently the position field).
    const showEditRelativeForm = (relative: Relative) =>
        setModal({
            title: t('permissions.relativesList.editUser'),
            content: (
                <EditRelativeForm
                    relative={relative}
                    clubSlug={clubSlug}
                    callback={() => {
                        asyncListRef.current?.reload();
                        resetModal();
                    }}
                />
            ),
            show: true
        });

    const handleRemoveRelative = useConfirmedCallback((relative: Relative) => {
        void api({
            url: `club/${clubSlug}/relatives/${relative.id}`,
            method: 'DELETE',
            onSuccess: () => asyncListRef.current?.reload()
        });
    });

    return (
        <DataTableContainer>
            <Button onPress={showAddRelativeForm}>{t('permissions.relativesList.addUser')}</Button>
            <DataTable
                asyncListRef={asyncListRef}
                fetchUrl={`club/${clubSlug}/relatives`}
                columns={columns}
                rowActions={(item) => (
                    <TableActionButton.Container justify="flex-end">
                        <TableActionButton
                            icon={faEdit}
                            color="warning"
                            tooltip={t('common:actions.edit')}
                            onClick={() => showEditRelativeForm(item)}
                        />
                        <TableActionButton
                            icon={faBalanceScaleRight}
                            tooltip={t('permissions.groupManagement.manageRights')}
                            onClick={() => showPermissionForm(item)}
                        />
                        <TableActionButton
                            icon={faLayerGroup}
                            tooltip={t('permissions.relativesList.manageGroups')}
                            onClick={() => showPermissionGroupAssignmentForm(item)}
                        />
                        <TableActionButton
                            icon={faGlasses}
                            tooltip={t('permissions.relativesList.viewPermissions')}
                            color="secondary"
                            onClick={() => showPermissionPreview(item)}
                        />
                        <TableActionButton
                            icon={faTrash}
                            tooltip={t('permissions.relativesList.removeUser')}
                            color="error"
                            onClick={() => handleRemoveRelative(item)}
                        />
                    </TableActionButton.Container>
                )}
            />
        </DataTableContainer>
    );
};
